import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type RaceWithClasses, type Boat, type MyEntry, ApiError } from '../../lib/api.ts';

export function MyRaces() {
  const qc = useQueryClient();
  const races = useQuery({
    queryKey: ['my-available-races'],
    queryFn: () => api.myAvailableRaces(),
  });
  const myBoats = useQuery({ queryKey: ['my-boats'], queryFn: () => api.myBoats() });
  const myEntries = useQuery({ queryKey: ['my-entries'], queryFn: () => api.myEntries() });

  function refresh() {
    qc.invalidateQueries({ queryKey: ['my-available-races'] });
    qc.invalidateQueries({ queryKey: ['my-entries'] });
  }

  if (races.isLoading || myBoats.isLoading || myEntries.isLoading) {
    return <p className="text-slate-500 text-sm">Laster…</p>;
  }

  const entriesByRace = new Map<string, MyEntry[]>();
  for (const e of myEntries.data ?? []) {
    const list = entriesByRace.get(e.raceId) ?? [];
    list.push(e);
    entriesByRace.set(e.raceId, list);
  }

  return (
    <div>
      <h1 className="text-lg font-semibold mb-4">Tilgjengelige regattaer</h1>

      {(races.data ?? []).length === 0 ? (
        <p className="text-slate-500 text-sm">Ingen åpne regattaer for øyeblikket.</p>
      ) : (
        <ul className="space-y-3">
          {(races.data ?? []).map((r) => (
            <RaceCard
              key={r.id}
              race={r}
              myBoats={myBoats.data ?? []}
              myEntries={entriesByRace.get(r.id) ?? []}
              onChange={refresh}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function RaceCard({
  race,
  myBoats,
  myEntries,
  onChange,
}: {
  race: RaceWithClasses;
  myBoats: Boat[];
  myEntries: MyEntry[];
  onChange: () => void;
}) {
  const allowedClassIds = race.classes.map((c) => c.classId);
  const allowedSet = new Set(allowedClassIds);

  const enrolledBoatIds = new Set(myEntries.map((e) => e.boatId));

  const eligibleBoats = myBoats.filter(
    (b) =>
      !enrolledBoatIds.has(b.id) &&
      (allowedSet.size === 0 || (b.classId && allowedSet.has(b.classId))),
  );

  const ineligibleBoats = myBoats.filter(
    (b) =>
      !enrolledBoatIds.has(b.id) &&
      allowedSet.size > 0 &&
      (!b.classId || !allowedSet.has(b.classId)),
  );

  const deadlinePassed = race.registrationDeadline
    ? new Date(race.registrationDeadline).getTime() < Date.now()
    : false;

  return (
    <li className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{race.name}</div>
          <div className="text-xs text-slate-400 font-mono">
            Start:{' '}
            {new Date(race.startTime).toLocaleString('no-NO', {
              dateStyle: 'medium',
              timeStyle: 'short',
              timeZone: 'Europe/Oslo',
            })}
          </div>
          {race.venue && <div className="text-xs text-slate-500">{race.venue}</div>}
          <div className="text-xs text-slate-500 mt-1">
            {race.classes.length > 0 ? (
              <>
                Tillatte klasser:{' '}
                <span className="text-slate-300">
                  {race.classes.map((c) => c.name).join(', ')}
                </span>
              </>
            ) : (
              <span className="italic">Åpen for alle klasser</span>
            )}
          </div>
          {race.registrationDeadline && (
            <div
              className={`text-xs mt-1 ${
                deadlinePassed ? 'text-red-400' : 'text-amber-400'
              }`}
            >
              Påmeldingsfrist:{' '}
              {new Date(race.registrationDeadline).toLocaleString('no-NO', {
                dateStyle: 'short',
                timeStyle: 'short',
                timeZone: 'Europe/Oslo',
              })}{' '}
              {deadlinePassed && '(passert)'}
            </div>
          )}
        </div>
        <span
          className={`text-xs px-2 py-1 rounded ${
            race.status === 'postponed'
              ? 'bg-amber-600/30 text-amber-300'
              : 'bg-sky-600/30 text-sky-300'
          }`}
        >
          {race.status === 'postponed' ? 'Utsatt' : 'Planlagt'}
        </span>
      </div>

      {/* Mine påmeldte båter for denne regattaen */}
      {myEntries.length > 0 && (
        <div className="border-t border-slate-800 pt-3">
          <div className="text-xs text-slate-400 mb-2">Mine påmeldte båter:</div>
          <ul className="space-y-1">
            {myEntries.map((e) => (
              <li
                key={e.id}
                className="flex items-center gap-2 text-sm bg-emerald-900/20 border border-emerald-800/50 rounded px-2 py-1.5"
              >
                <span className="font-mono text-xs text-emerald-300 w-16">{e.sailNumber}</span>
                <span className="flex-1 truncate">{e.boatName}</span>
                <WithdrawButton entryId={e.id} boatName={e.boatName} onChange={onChange} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Påmeldingsskjema */}
      {!deadlinePassed && eligibleBoats.length > 0 && (
        <EnrollForm raceId={race.id} boats={eligibleBoats} onEnrolled={onChange} />
      )}

      {deadlinePassed && (
        <div className="text-xs text-amber-300 bg-amber-900/20 border border-amber-800/50 rounded p-2">
          Påmeldingsfristen er passert. Kontakt regattakontoret hvis du må etterregistrere.
        </div>
      )}

      {ineligibleBoats.length > 0 && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer">
            {ineligibleBoats.length} av dine båter er ikke i tillatt klasse
          </summary>
          <ul className="mt-1 space-y-0.5 pl-4">
            {ineligibleBoats.map((b) => (
              <li key={b.id}>
                {b.sailNumber} {b.boatName}{' '}
                <span className="italic">
                  ({b.className || 'ingen klasse'})
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </li>
  );
}

function EnrollForm({
  raceId,
  boats,
  onEnrolled,
}: {
  raceId: string;
  boats: Boat[];
  onEnrolled: () => void;
}) {
  const [boatId, setBoatId] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const enroll = useMutation({
    mutationFn: async () => {
      if (!boatId) throw new Error('Velg en båt');
      return api.enrollMyBoat(raceId, boatId);
    },
    onSuccess: () => {
      setBoatId('');
      setErr(null);
      onEnrolled();
    },
    onError: (e: ApiError | Error) => setErr(e.message),
  });

  return (
    <div className="border-t border-slate-800 pt-3">
      <div className="flex gap-2">
        <select
          value={boatId}
          onChange={(e) => setBoatId(e.target.value)}
          className="flex-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
        >
          <option value="">— Velg båt å melde på —</option>
          {boats.map((b) => (
            <option key={b.id} value={b.id}>
              {b.sailNumber} • {b.boatName}
              {b.className ? ` [${b.className}]` : ''}
            </option>
          ))}
        </select>
        <button
          onClick={() => enroll.mutate()}
          disabled={!boatId || enroll.isPending}
          className="px-4 bg-sky-600 hover:bg-sky-500 rounded text-sm font-semibold disabled:opacity-50"
        >
          Meld på
        </button>
      </div>
      {err && <div className="text-xs text-red-300 mt-2">{err}</div>}
    </div>
  );
}

function WithdrawButton({
  entryId,
  boatName,
  onChange,
}: {
  entryId: string;
  boatName: string;
  onChange: () => void;
}) {
  const mut = useMutation({
    mutationFn: () => api.withdrawMyEntry(entryId),
    onSuccess: onChange,
    onError: (e: ApiError) => alert(e.message),
  });

  return (
    <button
      onClick={() => {
        if (confirm(`Avmelde ${boatName} fra regattaen?`)) {
          mut.mutate();
        }
      }}
      disabled={mut.isPending}
      className="text-xs text-red-400 disabled:opacity-50"
    >
      Avmeld
    </button>
  );
}
